import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { IsoCountry, PortalTypeEnum } from '@ats/api/cm';
import { Address, AddressType, CmInterceptHeaders } from '@ats/cm/core';
import { CMOption } from '@ats/cm/ui';
import { atsSort } from '@ats/utility';
import { SettingsService } from '@cm/core/settings';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Apollo, gql } from 'apollo-angular';
import { Observable, combineLatest } from 'rxjs';
import {
	combineLatestWith,
	debounceTime,
	distinctUntilChanged,
	map,
	skip,
	startWith,
	tap,
} from 'rxjs/operators';
import { CmValidators } from '../../validators';
import { AddressFormLocs } from './address-form.type';

const COUNTRY_QUERY = gql`
	query countries {
		countries {
			code
			name
			noSubdivision
			subdivisions {
				code
				name
			}
			auxiliary {
				id
				prohibited
				sortGroup
			}
		}
	}
`;

@UntilDestroy()
@Component({
	selector: 'cm-address-form',
	templateUrl: './cm-address-form.component.html',
	styleUrls: ['./cm-address-form.component.scss'],
	standalone: false,
})
export class CmAddressFormComponent implements OnInit, OnChanges {
	@Input() address: Address;
	@Input({ required: true }) locs!: AddressFormLocs;
	@Input() touchOnInit: boolean = false;
	@Output() save: EventEmitter<Address> = new EventEmitter<Address>();
	@Output() cancel: EventEmitter<void> = new EventEmitter();
	@Input() freeform: ('state' | 'country')[];
	@Input() portalType: PortalTypeEnum;
	@Input() hideCancel: boolean = false;
	@Input() hasVueValidation: boolean = false;
	@Input() hasCountryRestrictionFeature: boolean = false;

	addressType = AddressType;
	postalCodeMaxLength: number;
	formGroup: UntypedFormGroup;
	countries$: Observable<CMOption[]>;
	states$: Observable<CMOption[]>;
	displayedStateCharacters$: Observable<number>;
	classPrefix: string;
	locPrefix: string;
	stateRequired: boolean;

	constructor(
		private apollo: Apollo,
		private fb: UntypedFormBuilder,
		private settings: SettingsService
	) {}

	ngOnInit() {
		const countries$ = this.fetchCountries$();
		this.classPrefix = this.portalType === PortalTypeEnum.SponsorPortal ? 'sp-form' : 'cp-form';

		this.locPrefix =
			this.portalType === PortalTypeEnum.SponsorPortal
				? 'CandidateProfile.Addresses/Candidates.Addresses.'
				: 'CandidateProfile.Addresses/Profile.Addresses.';

		this.postalCodeMaxLength = this.hasVueValidation ? 15 : 90;

		this.formGroup = this.fb.group({
			line1: [this.address.line1 || null, Validators.maxLength(256)],
			line2: [this.address.line2 || null, Validators.maxLength(256)],
			line3: [this.address.line3 || null, Validators.maxLength(256)],
			city: [this.address.city || null, Validators.maxLength(256)],
			state: [this.address.state || null, Validators.maxLength(256)],
			postal: [this.address.postal || null, Validators.maxLength(this.postalCodeMaxLength)],
			country: [this.address.country || null, Validators.maxLength(256)],
		});

		if (this.address.type !== AddressType.LOCALIZED) {
			this.formGroup.addControl(
				'fax',
				new UntypedFormControl(this.address.fax, Validators.maxLength(90))
			);
			this.formGroup.addControl(
				'phone',
				new UntypedFormControl(this.address.phone, Validators.maxLength(90))
			);
			this.formGroup.addControl('business', new UntypedFormControl(this.address.business));
		}

		if (this.address.type === AddressType.MAILING) {
			this.line1.addValidators([Validators.required]);
			this.city.addValidators([Validators.required]);
			this.country.addValidators([Validators.required]);
		}

		this.countries$ = countries$.pipe(
			map(countries =>
				countries.map(
					country => ({ display: country.name, value: country.code } as CMOption)
				)
			)
		);

		const selectedCountry$ = this.country.valueChanges.pipe(
			startWith(this.country.value),
			distinctUntilChanged()
		);
		this.states$ = combineLatest([countries$, selectedCountry$]).pipe(
			map(([countries, selected]) => {
				const selectedCountry = countries.find(
					country => country.code === selected || country.name === selected
				);
				const states = ((selectedCountry || {}).subdivisions || []).map(
					subdivision =>
						({ display: subdivision.name, value: subdivision.code } as CMOption)
				);
				return states;
			}),
			tap(states => {
				this.stateRequired = (states || []).length > 0 || this.hasVueValidation;
				if (this.stateRequired) {
					this.state.setValidators([Validators.required, Validators.maxLength(256)]);
				} else {
					this.state.setValidators([Validators.maxLength(256)]);
				}
				this.state.updateValueAndValidity();
			})
		);

		if (this.hasVueValidation) {
			this.listenToCountryChangesForVue();
		}

		if (!this.freeform?.includes('state') && !this.freeform?.includes('country')) {
			selectedCountry$.pipe(skip(1), untilDestroyed(this)).subscribe(() => {
				this.state.setValue(null);
				this.state.updateValueAndValidity();
			});
		}

		this.markFormAsTouchedIfAddressHasValues();
		this.displayedStateCharacters$ = this.getDisplayedStateCharacters$();
		this.applyCountryRestriction();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.formGroup) {
			return;
		}

		if (
			changes['hasCountryRestrictionFeature'] ||
			(changes['address'] &&
				changes['address'].previousValue?.country !==
					changes['address'].currentValue?.country)
		) {
			this.applyCountryRestriction();
		}
	}

	public handleSubmit() {
		this.formGroup.markAllAsTouched();
		this.formGroup.updateValueAndValidity();
		if (this.formGroup.valid) {
			const formValue = { ...this.formGroup.value } as Address;
			if (this.country.disabled) {
				if (!(this.hasCountryRestrictionFeature && this.country.value)) {
					formValue.country = this.country.value;
				} else {
					delete formValue.country;
				}
			}
			this.save.emit(formValue);
			return true;
		}
	}

	get line1() {
		return this.formGroup.get('line1') as UntypedFormControl;
	}
	get line2() {
		return this.formGroup.get('line2') as UntypedFormControl;
	}
	get line3() {
		return this.formGroup.get('line3') as UntypedFormControl;
	}
	get city() {
		return this.formGroup.get('city') as UntypedFormControl;
	}
	get state() {
		return this.formGroup.get('state') as UntypedFormControl;
	}
	get postal() {
		return this.formGroup.get('postal') as UntypedFormControl;
	}
	get country() {
		return this.formGroup.get('country') as UntypedFormControl;
	}
	get business() {
		return this.formGroup.get('business') as UntypedFormControl;
	}
	get phone() {
		return this.formGroup.get('phone') as UntypedFormControl;
	}
	get fax() {
		return this.address.type !== AddressType.LOCALIZED
			? (this.formGroup.get('fax') as UntypedFormControl)
			: undefined;
	}

	get cityReq() {
		return this.address.type === AddressType.MAILING;
	}

	get line1Req() {
		return this.address.type === AddressType.MAILING;
	}

	private getDisplayedStateCharacters$(): Observable<number> {
		return this.state.valueChanges.pipe(
			debounceTime(300),
			distinctUntilChanged(),
			combineLatestWith(this.states$),
			map(([stateCode, allStates]) => {
				const state = allStates.find(state => state.value === stateCode);
				return state ? ((state || {}).display || '').length : (stateCode || '').length;
			})
		);
	}

	private markFormAsTouchedIfAddressHasValues(): void {
		const formHasValue = Object.keys(this.address)
			.filter(key => {
				switch (key) {
					case '__typename':
					case 'cmcId':
					case 'type':
						return false;
					default:
						return true;
				}
			})
			.some(key => !!this.address[key]);

		if (formHasValue || this.touchOnInit) {
			this.formGroup.markAllAsTouched();
		}
	}

	private fetchCountries$() {
		return this.apollo
			.watchQuery<{ countries: IsoCountry[] }>({
				query: COUNTRY_QUERY,
				context: {
					headers: CmInterceptHeaders.ClientHeader(this.settings.clientSnapshot),
				},
			})
			.valueChanges.pipe(
				map(results => results?.data?.countries),
				map(countries => countries.filter(country => !country.auxiliary?.prohibited)),
				map(countries =>
					countries.map(country => ({
						...country,
						auxiliary: country.auxiliary
							? {
									...country.auxiliary,
									sortGroup: country.auxiliary.sortGroup * -1,
							  }
							: null,
					}))
				),
				atsSort('auxiliary.sortGroup', 'name')
			);
	}

	private listenToCountryChangesForVue(): void {
		this.country.valueChanges
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				startWith(this.country.value),
				untilDestroyed(this)
			)
			.subscribe(country => {
				if (country === 'USA' || country === 'CAN') {
					this.postal.setValidators([
						Validators.required,
						CmValidators.vuePostalCode(country),
					]);
					this.postal.updateValueAndValidity();
					this.phone.setValidators([
						Validators.required,
						CmValidators.vueUsaPhoneFormat,
						CmValidators.vueZerosPhoneFormat,
					]);
					this.phone.updateValueAndValidity();
					return;
				}
				this.postal.setValidators([Validators.required]);
				this.postal.updateValueAndValidity();
				this.phone.setValidators([Validators.required, CmValidators.vueZerosPhoneFormat]);
				this.phone.updateValueAndValidity();
			});
	}

	private applyCountryRestriction(): void {
		if (this.hasCountryRestrictionFeature && this.country.value) {
			this.country.disable();
		} else {
			this.country.enable();
		}
	}
}
